import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, FileSignature, Send, ShieldCheck, ShieldAlert, FileText, Paperclip, Fingerprint, Lock, History, Trash2, X, AlertTriangle, ExternalLink, CheckCircle2, XCircle, Upload, Calendar } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft:                    { label: "Draft",                   color: "bg-[#1F2937] text-[#6B7280] border-[#374151]",                   dot: "bg-[#4B5563]",    icon: FileText },
  pending_review:           { label: "Pending review",          color: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30",             dot: "bg-[#F59E0B]",    icon: Send },
  approved:                 { label: "Approved",                color: "bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/50",             dot: "bg-[#00F5D4]",    icon: ShieldCheck },
  approved_with_amendments: { label: "Approved · amendments",   color: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/40",            dot: "bg-[#F59E0B]",    icon: ShieldCheck },
  rejected:                 { label: "Rejected · resubmit",     color: "bg-[#FF007A]/10 text-[#FF007A] border-[#FF007A]/30",            dot: "bg-[#FF007A]",    icon: ShieldAlert },
};

const OUTCOMES = [
  {
    key: "green",
    label: "Green",
    sublabel: "Approve — clean pass",
    detail: "Document is approved as-is. Content will be frozen for audit.",
    btn: "bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] border-[#00F5D4]",
    chip: "bg-[#00F5D4]",
  },
  {
    key: "yellow",
    label: "Yellow",
    sublabel: "Approve with amendments",
    detail: "Minor amendments needed. Document stays editable — no resubmission required.",
    btn: "bg-[#F59E0B] hover:bg-[#D97706] text-[#030712] border-[#F59E0B]",
    chip: "bg-[#F59E0B]",
  },
  {
    key: "red",
    label: "Red",
    sublabel: "Reject — resubmit required",
    detail: "Amendments needed. Document returns to draft and must be resubmitted for review.",
    btn: "bg-[#FF007A] hover:bg-[#CC0062] text-white border-[#FF007A]",
    chip: "bg-[#FF007A]",
  },
];

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const dialogCls = "rounded-none bg-[#111827] border-[#374151] text-white";
const inputCls = "rounded-none bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563] focus:ring-[#00F5D4]";
const labelCls = "text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]";
const btnPrimary = "rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold";

const Compliance = () => {
  const [docs, setDocs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("__all__");
  const [selected, setSelected] = useState(null);
  const [user, setUser] = useState(null);

  const [view, setView] = useState("documents");

  const [ledger, setLedger] = useState([]);
  const [ledgerLoaded, setLedgerLoaded] = useState(false);
  const [ledgerProject, setLedgerProject] = useState("");
  const [signOffOpen, setSignOffOpen] = useState(false);
  const [signOffForm, setSignOffForm] = useState({ project_id: "", change_description: "" });
  const [signOffBusy, setSignOffBusy] = useState(false);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", text_content: "", project_id: "__global__" });

  const [attachOpen, setAttachOpen] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({ outcome: "green", comments: "" });

  const load = useCallback(async () => {
    const [d, p, me] = await Promise.all([
      api.get("/compliance/documents"),
      api.get("/projects"),
      api.get("/auth/me"),
    ]);
    setDocs(d.data);
    setProjects(p.data);
    setUser(me.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/compliance/documents/${id}`);
    setSelected(data);
  };

  const refreshDetail = async () => {
    if (selected) await openDetail(selected.document_id);
    load();
  };

  const createDoc = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      const payload = {
        title: form.title,
        text_content: form.text_content,
        project_id: form.project_id === "__global__" ? null : form.project_id,
      };
      const { data } = await api.post("/compliance/documents", payload);
      setOpen(false);
      setForm({ title: "", text_content: "", project_id: "__global__" });
      toast.success("Document created");
      load();
      openDetail(data.document_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveEdit = async (patch) => {
    try {
      await api.patch(`/compliance/documents/${selected.document_id}`, patch);
      toast.success("Saved");
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitForReview = async () => {
    try {
      await api.post(`/compliance/documents/${selected.document_id}/submit`);
      toast.success("Submitted for review — content is now locked");
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doReview = async () => {
    try {
      const { data } = await api.post(`/compliance/documents/${selected.document_id}/review`, reviewForm);
      setReviewOpen(false);
      setReviewForm({ outcome: "green", comments: "" });
      const label = data.document_status === "approved" ? "approved (green)"
        : data.document_status === "approved_with_amendments" ? "approved with amendments (yellow)"
        : "rejected (red)";
      toast.success(`Document ${label}`);
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteDoc = async () => {
    if (!window.confirm("Delete this document? Audit history will be preserved.")) return;
    try {
      await api.delete(`/compliance/documents/${selected.document_id}`);
      setSelected(null);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadAttachment = async () => {
    if (!attachFile) { toast.error("Choose a file first"); return; }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", attachFile);
      await api.post(`/compliance/documents/${selected.document_id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachOpen(false);
      setAttachFile(null);
      toast.success("File uploaded");
      refreshDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadBusy(false);
    }
  };

  const removeAttachment = async (aid) => {
    await api.delete(`/compliance/documents/${selected.document_id}/attachments/${aid}`);
    refreshDetail();
  };

  const loadLedger = useCallback(async () => {
    try {
      const url = ledgerProject
        ? `/projects/${ledgerProject}/compliance-locks`
        : `/compliance-locks`;
      const { data } = await api.get(url);
      setLedger(data);
      setLedgerLoaded(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load ledger");
    }
  }, [ledgerProject]);

  useEffect(() => {
    if (view === "ledger") loadLedger();
  }, [view, loadLedger]);

  const submitSignOff = async () => {
    if (!signOffForm.project_id) { toast.error("Pick a project"); return; }
    if (!signOffForm.change_description.trim()) { toast.error("Describe the change being signed off"); return; }
    setSignOffBusy(true);
    try {
      await api.post(`/projects/${signOffForm.project_id}/compliance-locks`, {
        change_description: signOffForm.change_description,
      });
      toast.success("Cryptographic sign-off committed to the immutable ledger");
      setSignOffOpen(false);
      setSignOffForm({ project_id: "", change_description: "" });
      loadLedger();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sign-off failed");
    } finally {
      setSignOffBusy(false);
    }
  };

  const filteredDocs = filter === "__all__" ? docs : docs.filter((d) => d.status === filter);
  const projMap = Object.fromEntries(projects.map((p) => [p.project_id, p.name]));
  const canApprove = user?.role === "owner" || user?.role === "admin";
  const isLocked = selected && ["pending_review", "approved"].includes(selected.status);

  return (
    <DarkPage>
      <PageHeader
        eyebrow="PMO Governance · Compliance"
        title="Document workflow & audit trail"
        description="Draft → submit → approve, with cryptographic SHA-256 snapshots and reviewer IP capture on every signature."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className={btnPrimary} data-testid="new-compliance-doc-btn">
                <Plus className="w-4 h-4 mr-1" /> New document
              </Button>
            </DialogTrigger>
            <DialogContent className={`${dialogCls} sm:max-w-xl`}>
              <DialogHeader><DialogTitle className="font-display tracking-tight text-white">New compliance document</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className={labelCls}>Title</Label>
                  <Input className={`${inputCls} mt-1`} placeholder="e.g. Data Processing Agreement v3" data-testid="compliance-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                  <Label className={labelCls}>Scope</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="compliance-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#374151] text-white">
                      <SelectItem value="__global__">Tenant-wide policy (no project)</SelectItem>
                      {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelCls}>Content (Markdown allowed)</Label>
                  <Textarea rows={8} className={`${inputCls} mt-1 font-mono text-xs`} data-testid="compliance-content-input" value={form.text_content} onChange={(e) => setForm({ ...form, text_content: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createDoc} className={btnPrimary} data-testid="confirm-create-compliance-doc">Create draft</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8">
        {/* Tab switcher */}
        <div className="flex items-center justify-between mb-6 border-b border-[#1F2937]" data-testid="compliance-tabs">
          <div className="flex items-center gap-1">
            {[
              { key: "documents", label: "Documents",      icon: FileSignature },
              { key: "ledger",    label: "Sign-off Ledger", icon: Lock },
            ].map((t) => {
              const TabIcon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setView(t.key)}
                  className={`px-4 py-2 text-[11px] uppercase tracking-[0.25em] border-b-2 transition flex items-center gap-2 ${
                    view === t.key
                      ? "border-[#00F5D4] text-white font-semibold"
                      : "border-transparent text-[#6B7280] hover:text-white"
                  }`}
                  data-testid={`compliance-tab-${t.key}`}
                >
                  <TabIcon className="w-3 h-3" /> {t.label}
                </button>
              );
            })}
          </div>
          {view === "ledger" && canApprove && (
            <Dialog open={signOffOpen} onOpenChange={setSignOffOpen}>
              <DialogTrigger asChild>
                <Button className={btnPrimary} data-testid="new-signoff-btn">
                  <Fingerprint className="w-4 h-4 mr-1" /> Execute sign-off
                </Button>
              </DialogTrigger>
              <DialogContent className={`${dialogCls} sm:max-w-lg`}>
                <DialogHeader><DialogTitle className="font-display tracking-tight text-white">Cryptographic sign-off</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-xs text-[#9CA3AF]">A SHA-256 hash of <code className="font-mono text-[#00F5D4]">projectId | requestedBy | approvedBy | change | timestamp</code> is committed to the immutable ledger. Project status will be set to <strong className="text-white">Active - Compliance Audited</strong>.</p>
                  <div>
                    <Label className={labelCls}>Project</Label>
                    <Select value={signOffForm.project_id} onValueChange={(v) => setSignOffForm({ ...signOffForm, project_id: v })}>
                      <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="signoff-project-select"><SelectValue placeholder="Pick project" /></SelectTrigger>
                      <SelectContent className="bg-[#111827] border-[#374151] text-white">
                        {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelCls}>Change being approved</Label>
                    <Textarea
                      rows={4}
                      className={`${inputCls} mt-1`}
                      placeholder="e.g. Approved 12h scope creep on Build Phase 2 to absorb the Salesforce integration change request."
                      data-testid="signoff-change-input"
                      value={signOffForm.change_description}
                      onChange={(e) => setSignOffForm({ ...signOffForm, change_description: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submitSignOff} disabled={signOffBusy} className={btnPrimary} data-testid="confirm-signoff">
                    {signOffBusy ? "Signing…" : "Sign & commit to ledger"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {view === "ledger" ? (
          <div className="border border-[#1F2937] bg-[#111827]" data-testid="compliance-ledger">
            <div className="px-5 py-4 border-b border-[#1F2937] flex flex-wrap items-center gap-3">
              <Lock className="w-4 h-4 text-[#6B7280]" />
              <div className="font-display font-bold tracking-tight text-sm">Immutable sign-off ledger</div>
              <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-2">
                <Label className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] shrink-0">Project</Label>
                <Select value={ledgerProject || "__all__"} onValueChange={(v) => setLedgerProject(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="rounded-none h-8 text-xs flex-1 sm:w-56 bg-[#030712] border-[#374151] text-white" data-testid="ledger-project-filter"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#374151] text-white">
                    <SelectItem value="__all__">All projects (tenant)</SelectItem>
                    {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!ledgerLoaded ? (
              <div className="p-10 text-sm text-[#6B7280]">Loading ledger…</div>
            ) : ledger.length === 0 ? (
              <div className="p-12 text-center text-sm text-[#6B7280]" data-testid="ledger-empty">
                <Lock className="w-8 h-8 mx-auto mb-3 text-[#374151]" />
                No sign-off entries yet. Click <strong className="text-white">Execute sign-off</strong> to commit the first cryptographic ledger entry.
              </div>
            ) : (
              <div className="divide-y divide-[#1F2937]">
                {ledger.map((lk) => (
                  <div key={lk.lock_id} className="px-5 py-4" data-testid={`ledger-row-${lk.lock_id}`}>
                    <div className="flex items-start gap-4">
                      <div className="px-2 py-1 text-[10px] uppercase tracking-[0.15em] border bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/30 flex items-center gap-1">
                        <Fingerprint className="w-3 h-3" /> SHA-256
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{lk.change_description}</div>
                        <div className="mt-1 text-[11px] text-[#6B7280] font-mono break-all">
                          ▸ {projMap[lk.project_id] || "Project"} · requested by {lk.requested_by_name} · approved by {lk.approved_by_name} · {fmtTime(lk.timestamp)}
                        </div>
                        <div className="mt-2 text-[11px] sm:text-[10px] text-[#9CA3AF] font-mono bg-[#030712] border border-[#374151] px-2 py-1.5 break-all select-all leading-relaxed" data-testid={`ledger-hash-${lk.lock_id}`}>
                          {lk.cryptographic_hash}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-px bg-[#1F2937] border border-[#1F2937] min-h-[70vh]">
          {/* LIST */}
          <div className="bg-[#111827]">
            <div className="px-5 py-3 border-b border-[#1F2937] flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-[#6B7280]" />
              <div className="font-display font-bold tracking-tight text-sm">Documents <span className="text-[#4B5563] font-normal">({docs.length})</span></div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="rounded-none h-8 text-xs w-36 ml-auto bg-[#030712] border-[#374151] text-white" data-testid="compliance-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#374151] text-white">
                  <SelectItem value="__all__">All statuses</SelectItem>
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {filteredDocs.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#6B7280]" data-testid="compliance-empty">No documents yet.</div>
            ) : (
              <div className="divide-y divide-[#1F2937]">
                {filteredDocs.map((d) => {
                  const s = STATUS[d.status] || STATUS.draft;
                  const Icon = s.icon;
                  return (
                    <button key={d.document_id} onClick={() => openDetail(d.document_id)} className={`w-full text-left px-5 py-4 hover:bg-[#0D1117] transition-colors ${selected?.document_id === d.document_id ? "bg-[#0D1117]" : ""}`} data-testid={`compliance-row-${d.document_id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] border ${s.color} flex items-center gap-1`}>
                          <Icon className="w-3 h-3" /> {s.label}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate text-white">{d.title}</div>
                          <div className="text-[11px] text-[#6B7280] mt-0.5 font-mono">
                            {d.project_id ? <>▸ {projMap[d.project_id] || "Project"}</> : "▸ Tenant-wide"}
                            <span className="text-[#4B5563] ml-2">· {fmtTime(d.updated_at)}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* DETAIL */}
          <div className="bg-[#111827]">
            {!selected ? (
              <div className="p-10 text-center text-sm text-[#6B7280] h-full grid place-items-center" data-testid="compliance-detail-empty">
                <div>
                  <FileSignature className="w-10 h-10 mx-auto mb-3 text-[#374151]" />
                  Select a document to view, edit, submit for review, or approve.
                </div>
              </div>
            ) : (
              <DocDetail
                doc={selected}
                isLocked={isLocked}
                canApprove={canApprove}
                onClose={() => setSelected(null)}
                onSave={saveEdit}
                onSubmit={submitForReview}
                onOpenReview={() => setReviewOpen(true)}
                onDelete={deleteDoc}
                onOpenAttach={() => setAttachOpen(true)}
                onRemoveAttachment={removeAttachment}
              />
            )}
          </div>
        </div>
        )}
      </div>

      {/* Attachment dialog */}
      <Dialog open={attachOpen} onOpenChange={(o) => { setAttachOpen(o); if (!o) setAttachFile(null); }}>
        <DialogContent className={`${dialogCls} sm:max-w-md`}>
          <DialogHeader><DialogTitle className="font-display tracking-tight text-white">Upload an attachment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#9CA3AF]">File is streamed to tenant-scoped storage and renamed to a UUID — original filename is preserved for display only. Max 25 MB.</p>
            <label className="block border-2 border-dashed border-[#374151] hover:border-[#00F5D4] hover:bg-[#00F5D4]/5 transition-colors p-6 text-center cursor-pointer" data-testid="attach-dropzone">
              <Upload className="w-6 h-6 mx-auto text-[#4B5563] mb-2" />
              {attachFile ? (
                <>
                  <div className="text-sm font-medium text-white" data-testid="attach-filename">{attachFile.name}</div>
                  <div className="text-[11px] text-[#6B7280] font-mono mt-0.5">{(attachFile.size / 1024).toFixed(1)} KB</div>
                </>
              ) : (
                <>
                  <div className="text-sm text-[#9CA3AF]">Click to choose a file</div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5">PDF, DOCX, XLSX, PNG, ZIP…</div>
                </>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => setAttachFile(e.target.files?.[0] || null)}
                data-testid="attach-file-input"
              />
            </label>
          </div>
          <DialogFooter>
            <Button onClick={uploadAttachment} disabled={!attachFile || uploadBusy} className={btnPrimary} data-testid="confirm-attach">
              {uploadBusy ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className={`${dialogCls} sm:max-w-xl`}>
          <DialogHeader><DialogTitle className="font-display tracking-tight text-white flex items-center gap-2"><Fingerprint className="w-4 h-4" /> Sign off — traffic-light review</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-[#9CA3AF] bg-[#030712] p-3 border border-[#374151]">
              Your decision is permanently recorded with your name, IP address, and a SHA-256 hash of the current content.
            </div>
            <div>
              <Label className={`${labelCls} mb-2 block`}>Decision</Label>
              <div className="grid grid-cols-1 gap-2">
                {OUTCOMES.map((o) => {
                  const active = reviewForm.outcome === o.key;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setReviewForm({ ...reviewForm, outcome: o.key })}
                      className={`flex items-start gap-3 p-3 border text-left transition ${active ? "border-[#374151] bg-[#0D1117] ring-1 ring-[#374151]" : "border-[#1F2937] hover:border-[#374151]"}`}
                      data-testid={`outcome-${o.key}`}
                    >
                      <span className={`inline-block w-3 h-3 rounded-full mt-1 shrink-0 ${o.chip}`} />
                      <div className="flex-1">
                        <div className="text-sm font-semibold flex items-center gap-2 text-white">
                          {o.label}
                          <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-normal">{o.sublabel}</span>
                        </div>
                        <div className="text-xs text-[#9CA3AF] mt-1">{o.detail}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className={labelCls}>Reviewer comments</Label>
              <Textarea rows={3} className={`${inputCls} mt-1`} placeholder="Optional — required for yellow/red decisions in practice." data-testid="review-comments-input" value={reviewForm.comments} onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doReview} className={`rounded-none border ${OUTCOMES.find((o) => o.key === reviewForm.outcome).btn}`} data-testid="confirm-review">
              <Fingerprint className="w-4 h-4 mr-2" /> Sign &amp; record {OUTCOMES.find((o) => o.key === reviewForm.outcome).label.toLowerCase()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DarkPage>
  );
};

// ---------- Detail subcomponent ----------
const DocDetail = ({ doc, isLocked, canApprove, onClose, onSave, onSubmit, onOpenReview, onDelete, onOpenAttach, onRemoveAttachment }) => {
  const [title, setTitle] = useState(doc.title);
  const [content, setContent] = useState(doc.text_content || "");

  useEffect(() => {
    setTitle(doc.title);
    setContent(doc.text_content || "");
  }, [doc.document_id, doc.title, doc.text_content]);

  const dirty = title !== doc.title || content !== (doc.text_content || "");
  const s = STATUS[doc.status] || STATUS.draft;
  const expiryInfo = (() => {
    if (!doc.expiry_date) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const exp = new Date(doc.expiry_date); exp.setHours(0,0,0,0);
    const days = Math.round((exp - today) / 86400000);
    let tone = "bg-[#1F2937] text-[#6B7280] border-[#374151]";
    if (days < 0) tone = "bg-[#FF007A]/10 text-[#FF007A] border-[#FF007A]/30";
    else if (days <= 30) tone = "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30";
    else if (days <= 90) tone = "bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/30";
    return { days, tone, label: days < 0 ? `EXPIRED ${Math.abs(days)}d ago` : `Expires in ${days}d` };
  })();

  const inputCls = "rounded-none bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563] focus:ring-[#00F5D4]";
  const btnPrimary = "rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold";

  return (
    <div data-testid="compliance-detail-panel">
      <div className="px-5 py-3 border-b border-[#1F2937] flex items-center gap-2 flex-wrap">
        <div className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] border ${s.color}`}>{s.label}</div>
        {expiryInfo && (
          <div className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] border flex items-center gap-1 ${expiryInfo.tone}`} data-testid="expiry-badge" title={`Expiry date: ${doc.expiry_date}`}>
            <Calendar className="w-3 h-3" /> {expiryInfo.label}
          </div>
        )}
        <div className="text-xs text-[#4B5563] font-mono truncate ml-2">{doc.document_id}</div>
        <button onClick={onClose} className="ml-auto text-[#4B5563] hover:text-white" data-testid="close-compliance-detail">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-6 space-y-5">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Title</Label>
          <Input className={`${inputCls} mt-1 font-display font-bold text-lg`} value={title} onChange={(e) => setTitle(e.target.value)} disabled={isLocked} data-testid="detail-title-input" />
        </div>

        {isLocked && (
          <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] px-3 py-2 text-xs flex items-center gap-2" data-testid="lock-banner">
            <Lock className="w-3.5 h-3.5" /> Content is locked while {STATUS[doc.status].label.toLowerCase()}. Edits would invalidate the audit trail.
          </div>
        )}

        <div>
          <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Content</Label>
          <Textarea rows={10} className={`${inputCls} mt-1 font-mono text-xs`} value={content} onChange={(e) => setContent(e.target.value)} disabled={isLocked} data-testid="detail-content-input" />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {dirty && !isLocked && (
            <Button onClick={() => onSave({ title, text_content: content })} className={btnPrimary} data-testid="save-doc-btn">Save</Button>
          )}
          {doc.status === "draft" && !dirty && (
            <Button onClick={onSubmit} variant="outline" className="rounded-none border-[#374151] text-[#9CA3AF] hover:text-white bg-transparent" data-testid="submit-doc-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> Submit for review
            </Button>
          )}
          {doc.status === "pending_review" && canApprove && (
            <Button onClick={onOpenReview} className="rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold" data-testid="open-review-btn">
              <Fingerprint className="w-3.5 h-3.5 mr-1" /> Sign off
            </Button>
          )}
          {doc.status === "pending_review" && !canApprove && (
            <div className="text-xs text-[#6B7280]">Awaiting an admin to sign off.</div>
          )}
          {doc.status !== "approved" && (
            <button onClick={onDelete} className="ml-auto text-xs text-[#FF007A] hover:underline" data-testid="delete-doc-btn">Delete</button>
          )}
        </div>

        <div className="border border-[#374151] bg-[#030712] p-3 text-[11px] font-mono break-all" data-testid="content-hash">
          <div className="text-[#6B7280] uppercase tracking-[0.2em] text-[10px] mb-1 flex items-center gap-1"><Fingerprint className="w-3 h-3" /> Current SHA-256 hash</div>
          <span className="text-[#9CA3AF]">{doc.current_content_hash}</span>
        </div>

        <div className="border border-[#1F2937]">
          <div className="px-4 py-2 border-b border-[#1F2937] flex items-center gap-2">
            <Paperclip className="w-3.5 h-3.5 text-[#6B7280]" />
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280]">Attachments ({doc.attachments?.length || 0})</div>
            {!isLocked && (
              <Button variant="outline" className="rounded-none h-7 text-xs ml-auto border-[#374151] text-[#9CA3AF] hover:text-white bg-transparent" onClick={onOpenAttach} data-testid="add-attachment-btn">
                <Upload className="w-3 h-3 mr-1" /> Upload file
              </Button>
            )}
          </div>
          {doc.attachments?.length === 0 ? (
            <div className="px-4 py-3 text-xs text-[#4B5563]">No attachments.</div>
          ) : (
            <div className="divide-y divide-[#1F2937]">
              {doc.attachments.map((a) => {
                const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                const href = a.storage_url?.startsWith("/api/") ? `${apiBase}${a.storage_url}` : a.storage_url;
                return (
                <div key={a.attachment_id} className="flex items-center gap-3 px-4 py-2" data-testid={`attachment-${a.attachment_id}`}>
                  <Paperclip className="w-3 h-3 text-[#4B5563] shrink-0" />
                  <a href={href} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 text-sm hover:underline truncate flex items-center gap-1 text-[#9CA3AF] hover:text-white">
                    {a.file_name} <ExternalLink className="w-3 h-3 text-[#4B5563]" />
                  </a>
                  <div className="text-[10px] text-[#4B5563] font-mono">{(a.file_size_bytes / 1024).toFixed(1)} KB</div>
                  {!isLocked && (
                    <button onClick={() => onRemoveAttachment(a.attachment_id)} className="text-[#374151] hover:text-[#FF007A]" data-testid={`remove-attachment-${a.attachment_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border border-[#1F2937]">
          <div className="px-4 py-2 border-b border-[#1F2937] flex items-center gap-2">
            <History className="w-3.5 h-3.5 text-[#6B7280]" />
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280]">Audit ledger ({doc.audit_history?.length || 0})</div>
          </div>
          {doc.audit_history?.length === 0 ? (
            <div className="px-4 py-3 text-xs text-[#4B5563]">No reviews yet.</div>
          ) : (
            <div className="divide-y divide-[#1F2937]">
              {doc.audit_history.map((a) => {
                const sm = STATUS[a.action_taken] || STATUS.draft;
                return (
                  <div key={a.audit_id} className="px-4 py-3" data-testid={`audit-row-${a.audit_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={`px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] border ${sm.color}`}>{sm.label}</div>
                      <div className="text-sm font-medium">{a.approver_name}</div>
                      <div className="text-[11px] text-[#6B7280] font-mono ml-auto">{fmtTime(a.actioned_at)}</div>
                    </div>
                    {a.reviewer_comments && <div className="text-xs text-[#9CA3AF] mt-1.5 italic">"{a.reviewer_comments}"</div>}
                    <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] font-mono text-[#6B7280]">
                      <div>
                        <div className="uppercase tracking-[0.2em] text-[#4B5563]">IP</div>
                        <div className="break-all">{a.ip_address || "—"}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-[0.2em] text-[#4B5563]">Snapshot hash</div>
                        <div className="break-all">{a.document_content_snapshot_hash?.slice(0, 24)}…</div>
                      </div>
                    </div>
                    {a.content_unchanged === false && (
                      <div className="mt-2 text-[11px] text-[#FF007A] flex items-center gap-1.5 bg-[#FF007A]/10 px-2 py-1 border border-[#FF007A]/30" data-testid={`tamper-warning-${a.audit_id}`}>
                        <AlertTriangle className="w-3 h-3" /> Document content has been modified since this signature.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Compliance;
